import { Fragment } from 'react';
import { t, tn } from '@app/i18n';
import { Alert, Badge, Chip, InfoTip } from '@zarya/ui';
import { operatorText } from '@app/main/Policies/PolicyModel';
import { diagnosticText, type DiagnosticTarget } from './diagnosticText';
import {
  BARRIER_KEYS,
  barrier,
  causaVocab,
  RULES_SHOWN,
  simulatedBarrierTip,
  a2aExplanation,
  barriersOf,
  causaOf,
  contractGrants,
  isUngoverned,
  orderedRules,
  ruleGrants,
  valueText,
  whatWouldPass,
  type Barriers,
  type Causa,
  type CausaContext,
  type Diagnostic,
  type RuleEvaluated,
} from './causa';
import { a2aTip, type DecisionKind } from './vocabulario';
import s from './Decisions.module.css';

/**
 * **Por que negou** — o bloco de T17 (e o mesmo bloco em T18, quando a simulação
 * traz diagnóstico). Uma negação deixa de ser «DENY + frase» e passa a dizer
 * onde ela parou: numa barreira antes dos eixos, no eixo workload, ou no eixo
 * pessoa.
 *
 * ## O que este componente NÃO faz
 *
 * Não decide nada. A causa vem do PDP; as três seções são leitura do
 * `diagnostico` como ele veio; e «O que faria passar» reescreve em português o
 * que já está escrito lá (`causa.ts › whatWouldPass`). Quando o diagnóstico não
 * vem — decisão de um executor anterior a esta versão, `causa: unknown` — o
 * bloco diz **isso**, em vez de deduzir a explicação do veredito. Uma
 * explicação inventada é pior que nenhuma: ela é convincente.
 *
 * ## Por que as três seções aparecem mesmo satisfeitas
 *
 * Numa negação o eixo que passou é metade da informação: «o contrato tem a tool,
 * o que faltou foi regra para a pessoa» é o que ensina o modelo dos dois eixos a
 * quem levou o 403. Mostrar só o que barrou deixaria o operador procurando o
 * problema na metade certa por acaso.
 */

export interface WhyDeniedProps {
  causa: Causa | null | undefined;
  diagnostico: Diagnostic | null | undefined;
  /** Atalho do detalhe; cai em `diagnostico.barreiras` quando ausente. */
  barreiras?: Barriers | null;
  /** Tool, identidade e claims para as frases de «O que faria passar». */
  contexto?: CausaContext;
  /**
   * A frase humana, quando ela NÃO vem dentro do diagnóstico.
   *
   * Em T17 ela vem: o coletor copia `diagnostico.detail`. No `simulate` (T18) o mesmo texto é campo
   * de topo da resposta (`detail`), não do diagnóstico — e sem esta porta ele sumia da tela junto
   * com o Alert que o mostrava antes. `diagnostico.detail` continua tendo precedência: é a frase
   * que veio grudada no objeto que explica.
   */
  frase?: string | null;
  /** `false` esconde o cabeçalho (quem já tem um, como o card de T18). */
  titulo?: boolean;
  /**
   * O que o PDP decidiu (DEF-34, revisto no DEF-29 em 09/09).
   *
   * Com `a2a` **e sem diagnóstico** o bloco troca os eixos pela explicação da
   * delegação, e **nunca** diz «executor anterior a esta versão» — até 08/09 uma
   * A2A não trazia diagnóstico por desenho, e a frase da idade seria falsa sobre
   * as decisões daquela época.
   *
   * Com `a2a` **e diagnóstico** ele desenha os MESMOS dois eixos de uma tool: é o
   * ponto do DEF-29 — mesma avaliação, mesma explicação —, lendo `contract_agents`
   * e `grants_agent` onde a tool lê `contract_tools` e `grants_tool`.
   */
  kind?: DecisionKind | null;
  /** O agente destino da delegação (`Decision.agent`). */
  agent?: string | null;
  /** O veredito da linha, para a frase da delegação e para o título do bloco. */
  verdict?: string | null;
  /**
   * A linha é a simulação em lote da lente «Ver como»: simulada e sem tool. Quem decide é a LINHA da lista (§3.2 do
   * protótipo do aim-6ph.4), e não o detalhe.
   */
  simulatedBatch?: boolean;
  /** O método da chamada, para a frase da chamada de sessão (`initialize`, `tools/list`). */
  rpcMethod?: string | null;
  /** A linha é uma chamada de controle do executor (D1, aim-6ph.8): o PDP confere só o certificado. */
  controlCall?: boolean;
  /** O detalhe é de uma linha guardada antes do aim-6ph.8, que não tem o diagnóstico (`detail_unavailable`). */
  notStored?: boolean;
}

/**
 * O título do bloco (§3.1 do protótipo do aim-6ph.4): «Por que permitiu» num ALLOW, «Por que negou» num DENY, e «Por
 * que decidiu» no indeterminado e na delegação A2A sem diagnóstico, como já era nela. A delegação COM diagnóstico tem o
 * mesmo bloco de uma tool (DEF-29), e o título dela segue o veredito como o da tool. Até aqui o bloco dizia «Por que
 * negou» sobre uma decisão permitida.
 */
function whyTitle(verdict: string | null | undefined, delegationWithoutDiagnosis: boolean): string {
  if (delegationWithoutDiagnosis || verdict === 'indeterminado') return t('decisions.why.titleA2A');
  if (verdict === 'permitido') return t('decisions.why.labelAllowed');
  return t('decisions.why.label');
}

/**
 * O caso da decisão sem diagnóstico (§3.2 do protótipo do aim-6ph.4), na ordem que o protótipo dá: a simulação em
 * lote, a chamada de controle, a chamada de sessão, a linha anterior ao detalhe guardado, e o resto. O texto antigo
 * culpava «uma versão anterior do middleware», o que era falso em todos.
 */
function noAxesText({
  simulatedBatch,
  controlCall,
  kind,
  rpcMethod,
  notStored,
}: {
  simulatedBatch: boolean;
  controlCall: boolean;
  kind: DecisionKind | null | undefined;
  rpcMethod: string | null | undefined;
  notStored: boolean;
}): {
  key: 'simulatedBatch' | 'controlCall' | 'session' | 'notStored' | 'notPublished';
  text: string;
} {
  if (simulatedBatch) {
    return { key: 'simulatedBatch', text: t('decisions.why.noAxes.simulatedBatch') };
  }
  if (controlCall) {
    return {
      key: 'controlCall',
      text: t('decisions.why.noAxes.controlCall', { method: rpcMethod || '—' }),
    };
  }
  if (kind === 'session') {
    return {
      key: 'session',
      text: t('decisions.why.noAxes.session', { method: rpcMethod || '—' }),
    };
  }
  if (notStored) return { key: 'notStored', text: t('decisions.why.noAxes.notStored') };
  return { key: 'notPublished', text: t('decisions.why.noAxes.notPublished') };
}

/**
 * Estado de uma barreira — **quatro estados**, não três (contrato v3).
 *
 * `"simulated"` não é «ok» nem «não avaliada», e cair em qualquer um dos dois
 * seria mentir em direções opostas: «ok» afirmaria uma verificação que não
 * houve, e «não avaliada» afirmaria que o PDP parou antes — quando na verdade
 * ele chegou à barreira e leu as claims sem assinatura para conferir. Enquanto
 * o tipo era `boolean | null`, era o segundo que acontecia, em silêncio.
 *
 * `warning` e não `success`/`neutral` pela mesma razão: o estado é uma ressalva,
 * e a tela tem de dizer isso sem chamá-lo de falha.
 */
/**
 * Uma frase com **um** trecho em `<span mono>` no meio dela.
 *
 * A frase inteira vive numa chave só, com o marcador `{field}`, em vez de ser
 * partida em duas: partir obriga o tradutor a manter a ordem do português, e é
 * exatamente a ordem que muda entre idiomas.
 */
function NoteWithField({ chave, campo }: { chave: string; campo: string }) {
  const [antes, depois] = t(chave).split('{field}');
  return (
    <>
      {antes}
      <span className={s.mono}>{campo}</span>
      {depois}
    </>
  );
}

function BarrierState({ valor }: { valor: boolean | 'simulated' | null | undefined }) {
  if (valor === true) return <Badge tone="success">{t('decisions.why.barrier.ok')}</Badge>;
  if (valor === false) return <Badge tone="danger">{t('decisions.why.barrier.blocked')}</Badge>;
  if (valor === 'simulated') {
    return (
      <span className={s.veredito}>
        <Badge tone="warning">{t('decisions.why.barrier.simulated')}</Badge>
        <InfoTip
          text={simulatedBarrierTip()}
          label={t('decisions.why.barrier.simulated.label')}
          size="xs"
        />
      </span>
    );
  }
  // `null` é «não avaliada», que não é «passou»: o PDP parou antes de chegar nela.
  return <Badge tone="neutral">{t('decisions.why.barrier.notEvaluated')}</Badge>;
}

/**
 * Estado de um eixo — **tri-estado**, pela mesma razão que o vínculo
 * token↔workload é tri-estado em T17.
 *
 * `null` é «não avaliado»: quando uma barreira barra, o PDP para ali e os eixos
 * nunca chegam a ser consultados. Chamá-los de «não satisfeito» seria afirmar
 * que foram avaliados e não concederam — uma acusação diferente, e falsa, sobre
 * um contrato que pode estar perfeito.
 */
function AxisState({ satisfeito }: { satisfeito: boolean | null | undefined }) {
  if (satisfeito === true) return <Badge tone="success">{t('decisions.why.axis.satisfied')}</Badge>;
  if (satisfeito === false)
    return <Badge tone="danger">{t('decisions.why.axis.notSatisfied')}</Badge>;
  return <Badge tone="neutral">{t('decisions.why.axis.notEvaluated')}</Badge>;
}

/**
 * Por que esta regra não valeu — a primeira bandeira falsa, na ordem em que o PDP
 * olha.
 *
 * `delegacao` só troca a **palavra**: a bandeira é a mesma («a regra afirma o que
 * se pediu?»), e ela chega em `grants_agent` numa decisão A2A. Dizer «não afirma
 * esta tool» sobre uma delegação mandaria o operador procurar uma tool que a
 * chamada nunca citou.
 */
function ruleReason(regra: RuleEvaluated, delegacao: boolean): string {
  const grants = ruleGrants(regra);
  if (regra.covers_identity === false) return t('decisions.why.rule.reason.doesNotCover');
  if (grants === false)
    return t(
      delegacao
        ? 'decisions.why.rule.reason.doesNotGrantDelegation'
        : 'decisions.why.rule.reason.doesNotGrant',
    );
  if (regra.conditions_match === false) return t('decisions.why.rule.reason.conditionsFail');
  if (regra.conditions_match === true && grants === true)
    return t('decisions.why.rule.reason.satisfied');
  return t('decisions.why.rule.reason.unknown');
}

/**
 * O digest da release aprovada, curto.
 *
 * `contract_release` é um **digest**, não um número de versão: o documento que o
 * middleware publica não traz um. Escrever «contrato v3» a partir daqui seria
 * inventar o rótulo — quem o resolve é o produto, no histórico de aprovação da
 * identidade, e isso é uma consulta que esta tela ainda não faz. Até fazer, o
 * digest curto é o que existe, e ele identifica a release sem mentir sobre ela.
 */
function shortRelease(release: string | null | undefined): string | null {
  const texto = (release ?? '').trim();
  if (!texto) return null;
  const semPrefixo = texto.includes(':') ? texto.slice(texto.lastIndexOf(':') + 1) : texto;
  return semPrefixo.length > 12 ? semPrefixo.slice(0, 12) : semPrefixo;
}

export function WhyDenied({
  causa,
  diagnostico,
  barreiras,
  contexto = {},
  frase,
  titulo = true,
  kind,
  agent,
  verdict,
  simulatedBatch = false,
  controlCall = false,
  notStored = false,
  rpcMethod,
}: WhyDeniedProps) {
  const resolved = causaOf(causa);
  const vocab = causaVocab(resolved);
  // DEF-29 (09/09): uma delegação é capability, e é avaliada pelos mesmos dois
  // eixos. O que muda no bloco são duas palavras e duas chaves — não o desenho.
  const delegation = kind === 'a2a';
  const title = whyTitle(verdict, delegation && !diagnostico);

  // Marco 4 (qa-i18n): a frase principal nasce da ESTRUTURA (causa + flags dos eixos + alvo),
  // no idioma ativo. O `detail` do diagnóstico e a `frase` do simulate são o texto do PDP em
  // pt-BR — dado do executor: ficam numa linha secundária, nunca como a frase principal.
  const alvo: DiagnosticTarget = delegation
    ? { kind: 'agent', name: agent ?? contexto.agent ?? null }
    : { kind: 'tool', name: contexto.tool ?? null };
  const composta = diagnosticText(causa, diagnostico, alvo);
  const textoDoPdp = diagnostico?.detail ?? frase ?? null;

  // Delegação **sem diagnóstico** (DEF-34, o que ficou dele): decisão de um
  // executor anterior ao DEF-29, que decidia A2A antes dos eixos e não publicava
  // nenhum. O bloco de barreiras/eixos não se aplica — e o Alert de «versão
  // anterior do middleware» seria falso, porque naquele desenho o diagnóstico não
  // faltava: não existia para esta decisão.
  if (delegation && !diagnostico) {
    const explanation = a2aExplanation({
      verdict,
      causa,
      caller: contexto.identity ?? null,
      agent,
    });
    return (
      <section className={s.secao} aria-label={title}>
        {titulo ? (
          <h3 className={s.secaoTitulo}>
            {title}
            <InfoTip text={a2aTip()} label={t('decisions.why.a2a.tipLabel')} size="xs" />
          </h3>
        ) : null}
        <div className={s.causaBloco}>
          <Badge tone="primary">A2A</Badge>
          <Badge tone={vocab.tone} tip={vocab.tip}>
            {vocab.rotulo}
          </Badge>
          {composta ? <span className={s.causaFrase}>{composta}</span> : null}
        </div>
        {textoDoPdp ? (
          <p className={s.eixoNota} data-testid="texto-do-pdp">
            {t('decisions.diagnostic.pdpText')} <code>{textoDoPdp}</code>
          </p>
        ) : null}
        <div className={s.a2aBloco} role="note" data-testid="a2a-explanation">
          <p className={s.a2aLinha}>
            <strong>{explanation.modelo}</strong>
          </p>
          {explanation.veredito ? <p>{explanation.veredito}</p> : null}
          <p className={s.eixoNota}>
            <NoteWithField chave="decisions.why.a2a.note" campo={t('decisions.why.a2a.field')} />
          </p>
        </div>
      </section>
    );
  }
  // Fora do padrão (aim-uig.40 §6): «a causa e a frase, e mais nada». Não há eixos nem
  // barreiras a mostrar, por desenho — e o Alert «sem diagnóstico» abaixo diria que a
  // decisão veio de uma versão anterior do middleware, o que aqui é falso.
  if (isUngoverned(resolved)) {
    return (
      <section className={s.secao} aria-label={title}>
        {titulo ? <h3 className={s.secaoTitulo}>{title}</h3> : null}
        <div className={s.causaBloco}>
          <Badge tone={vocab.tone} tip={vocab.tip}>
            {vocab.rotulo}
          </Badge>
          {composta ? <span className={s.causaFrase}>{composta}</span> : null}
        </div>
        {textoDoPdp ? (
          <p className={s.eixoNota} data-testid="texto-do-pdp">
            {t('decisions.diagnostic.pdpText')} <code>{textoDoPdp}</code>
          </p>
        ) : null}
      </section>
    );
  }
  const barreira = barriersOf(barreiras ?? undefined, diagnostico);
  const workload = diagnostico?.workload_axis ?? null;
  const person = diagnostico?.person_axis ?? null;
  // Eixo não avaliado não ganha o detalhe (contrato vX, tools, regras): o
  // diagnóstico não fala dele, e desenhar «o contrato não concede nenhuma tool»
  // sobre um contrato que ninguém consultou é inventar um segundo problema.
  const workloadAvaliado = workload?.satisfied === true || workload?.satisfied === false;
  const personAvaliado = person?.satisfied === true || person?.satisfied === false;
  // Ordenadas por proximidade e recortadas: `evaluated_rules` pode ter dezenas
  // num tenant grande, e a que quase passou é a única acionável.
  const rules = orderedRules(person?.evaluated_rules ?? []);
  const rulesShown = rules.slice(0, RULES_SHOWN);
  const rulesHidden = rules.length - rulesShown.length;
  // O que o contrato concede: tools, ou as delegações numa decisão A2A.
  const granted = contractGrants(workload);
  // O que a chamada pediu — a tool, ou o agente alvo. É ele que fica realçado na
  // lista e nomeado na frase «não está nesta lista».
  const asked = delegation ? (agent ?? contexto.agent ?? null) : (contexto.tool ?? null);
  const suggestions = whatWouldPass(
    resolved,
    diagnostico,
    // O alvo entra no contexto quando é delegação: sem ele as frases mandariam
    // acrescentar «a tool pedida» ao contrato, numa chamada que não pediu tool.
    delegation && asked ? { ...contexto, agent: asked } : contexto,
  );
  // «Classificada» = o PDP disse a causa. `unknown` com diagnóstico presente é o
  // PDP a meio caminho do contrato (publica os eixos, ainda não a causa): há o
  // que mostrar, e o chip «sem diagnóstico» seria falso.
  const classificada = resolved !== 'unknown' || !diagnostico;
  const noAxes = diagnostico
    ? null
    : noAxesText({ simulatedBatch, controlCall, kind, rpcMethod, notStored });

  return (
    <section className={s.secao} aria-label={title}>
      {titulo ? (
        <h3 className={s.secaoTitulo}>
          {title}
          <InfoTip
            text={t('decisions.why.cause.tip')}
            label={t('decisions.why.cause.tipLabel')}
            size="xs"
          />
        </h3>
      ) : null}

      {/*
        O chip só aparece quando há causa. `unknown` COM diagnóstico não é «sem
        diagnóstico» — é um PDP que já publica os eixos e ainda não publica a
        causa, que é o estado do rollout enquanto o Rego novo não sobe em todo
        lugar. Chipar «sem diagnóstico» logo acima de um diagnóstico completo
        seria a tela contradizendo a si mesma na mesma tela.
      */}
      <div className={s.causaBloco}>
        {/* O selo da espécie: os eixos são os mesmos, o que a chamada pediu não é.
            Sem ele, um bloco falando de «delegação» apareceria sem nada dizer que
            esta decisão foi uma. */}
        {delegation ? (
          <Badge tone="primary" tip={a2aTip()}>
            A2A
          </Badge>
        ) : null}
        {classificada ? (
          <Badge tone={vocab.tone} tip={vocab.tip}>
            {vocab.rotulo}
          </Badge>
        ) : null}
        {composta ? <span className={s.causaFrase}>{composta}</span> : null}
      </div>
      {textoDoPdp ? (
        <p className={s.eixoNota} data-testid="texto-do-pdp">
          {t('decisions.diagnostic.pdpText')} <code>{textoDoPdp}</code>
        </p>
      ) : null}

      {/* Sem diagnóstico não há eixos a mostrar — e dizer POR QUÊ é o conteúdo. */}
      {noAxes ? (
        <div data-testid="why-no-axes" data-case={noAxes.key}>
          <Alert tone="info">{noAxes.text}</Alert>
        </div>
      ) : (
        <>
          {barreira ? (
            <div className={s.eixo}>
              <h4 className={s.eixoTitulo}>
                {t('decisions.why.barriers.title')}
                <InfoTip
                  text={t('decisions.why.barriers.tip')}
                  label={t('decisions.why.barriers.tipLabel')}
                  size="xs"
                />
              </h4>
              <dl className={s.defs}>
                {BARRIER_KEYS.map((chave) => (
                  <Fragment key={chave}>
                    <dt>{barrier(chave).rotulo}</dt>
                    <dd>
                      <BarrierState valor={barreira[chave]} />
                    </dd>
                  </Fragment>
                ))}
              </dl>
            </div>
          ) : null}

          <div className={s.eixo}>
            <h4 className={s.eixoTitulo}>
              {t('decisions.why.workload.title')}
              <AxisState satisfeito={workload?.satisfied} />
            </h4>
            {!workloadAvaliado ? (
              <p className={s.eixoNota}>{t('decisions.why.workload.blockedBefore')}</p>
            ) : (
              <>
                <p className={s.eixoNota}>
                  {t('decisions.why.workload.contract')}
                  {shortRelease(workload?.contract_release) ? (
                    <>
                      {' '}
                      {t('decisions.why.workload.release', {
                        digest: shortRelease(workload?.contract_release) ?? '',
                      })}{' '}
                      <InfoTip
                        text={t('decisions.why.workload.release.tip')}
                        label={t('decisions.why.workload.release.tipLabel')}
                        size="xs"
                      />
                    </>
                  ) : null}{' '}
                  {workload?.source ? (
                    <>{t('decisions.why.workload.source', { source: workload.source })}</>
                  ) : null}
                </p>
                {granted?.length ? (
                  <div className={s.chips}>
                    {granted.map((item) => (
                      <Chip key={item} selected={item === asked}>
                        {item}
                      </Chip>
                    ))}
                  </div>
                ) : (
                  <p className={s.vazio}>
                    {t(
                      delegation
                        ? 'decisions.why.workload.noDelegations'
                        : 'decisions.why.workload.noTools',
                    )}
                  </p>
                )}
                {asked && !granted?.includes(asked) ? (
                  <p className={s.eixoNota}>
                    {t(
                      delegation
                        ? 'decisions.why.workload.askedDelegation'
                        : 'decisions.why.workload.askedTool',
                    )}
                    {' ('}
                    <span className={s.mono}>{asked}</span>
                    {t('decisions.why.workload.askedSuffix')}
                  </p>
                ) : null}
              </>
            )}
          </div>

          <div className={s.eixo}>
            <h4 className={s.eixoTitulo}>
              {t('decisions.why.person.title')}
              <AxisState satisfeito={person?.satisfied} />
            </h4>
            {!personAvaliado ? (
              <p className={s.eixoNota}>{t('decisions.why.person.blockedBefore')}</p>
            ) : (
              <>
                {person?.rule ? (
                  <p className={s.eixoNota}>
                    {t('decisions.why.person.satisfiedBy', { rule: person.rule })}
                  </p>
                ) : null}

                {rules.length ? (
                  <>
                    <ul className={s.regras}>
                      {rulesShown.map((regra, i) => (
                        <li className={s.regra} key={`${regra.name ?? 'regra'}-${i}`}>
                          <span className={s.mono}>
                            {regra.name ?? t('decisions.why.rule.noName')}
                          </span>
                          <span className={s.regraMotivo}>{ruleReason(regra, delegation)}</span>
                          {regra.failed_condition ? (
                            <span className={s.condicao} data-testid="why-failed-condition">
                              {regra.failed_condition.claim ??
                                t('decisions.why.rule.claimFallback')}{' '}
                              {operatorText(regra.failed_condition.operator, '=')}{' '}
                              <b>{valueText(regra.failed_condition.expected)}</b>
                              {t('decisions.why.rule.received')}
                              <b>{valueText(regra.failed_condition.received)}</b>
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                    {rulesHidden > 0 ? (
                      <p className={s.eixoNota}>{tn('decisions.why.rules.more', rulesHidden)}</p>
                    ) : null}
                  </>
                ) : person?.satisfied ? null : (
                  // Só quando o eixo NÃO foi satisfeito. Com `satisfeito: true` e a
                  // regra nomeada acima, esta frase contradiria a linha anterior —
                  // e a lista vazia ali significa outra coisa: o diagnóstico
                  // nomeou a regra que valeu sem detalhar as demais.
                  <p className={s.vazio}>
                    {delegation
                      ? t('decisions.why.person.noRulesDelegation')
                      : t('decisions.why.person.noRules')}
                  </p>
                )}
              </>
            )}
          </div>
        </>
      )}

      {suggestions.length ? (
        <Alert tone="info" title={t('decisions.why.wouldPass.title')}>
          <ul className={s.sugestoes}>
            {suggestions.map((frase) => (
              <li key={frase}>{frase}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
    </section>
  );
}
